import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useState,
  useMemo,
  useEffect,
  useCallback,
  Fragment,
} from "react"
import { Button } from "react-bootstrap"
import {
  useTable,
  useSortBy,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce,
  usePagination,
  useRowSelect,
  useExpanded,
} from "react-table"

import { API_STATUS } from "./utils"
import { getFormattedMessage } from "../../components/shared_components_registration"
import { logToServer } from "../../ErrorBoundary"
import PageLoadingPanel from "./PageLoadingPanel"
import PageErrorPanel from "./PageErrorPanel"
import ReactSelectionElement from "./ReactSelectionElement"

export const onBooleanSort = (rowA, rowB, columnId) => {
  const a = rowA.original[columnId] ? 1 : 0
  const b = rowB.original[columnId] ? 1 : 0
  return a - b // Sorts false first, then true
}

export const UniversalFilter = ({
  column,
  setFilter,
  preFilteredRows = [],
  globalFilter,
  setGlobalFilter,
  filterType = "default",
  isClearable = true,
  customOptions, // An array of custom option objects
  updateMetricsWithFilter, // For carrying out an update required by the parent component
  shouldBeNumeric, // If true, only allow numeric input
}) => {
  const [value, setValue] = useState(filterType === "global" ? globalFilter : column?.filterValue)

  // Create a debounced function to update the filter
  const onChange = useAsyncDebounce((value) => {
    const columnName = filterType === "global" ? "globalFilter" : column?.id

    if (filterType === "global") {
      setGlobalFilter(value || undefined)
    } else {
      setFilter(value || undefined)
    }

    updateMetricsWithFilter?.(columnName, value)
  }, 500) // 500ms delay

  // Memoize the final options to prevent unnecessary recalculations
  // The id values are unimportant for LanzaLake implementation
  const finalOptions = useMemo(() => {
    const allOption = [{ value: "", text: "All", id: "general.all" }]

    if (customOptions) {
      return [...allOption, ...customOptions]
    }

    if (filterType === "boolean") {
      return [
        ...allOption,
        { value: "true", text: "True", id: "general.true" },
        { value: "false", text: "False", id: "general.false" },
      ]
    } else if (filterType === "select") {
      const optionSet = new Set()

      preFilteredRows.forEach((row) => {
        let val = row.values[column.id]
        if (val) optionSet.add(val)
      })
      return [...allOption, ...[...optionSet].map((option) => ({ value: option, text: option }))]
    }

    // For other filter types, return null (we'll use this to determine whether to render a select or input)
    return null
  }, [filterType, column, preFilteredRows, customOptions])

  // Render select input if finalOptions is not null, otherwise render text input
  if (finalOptions) {
    const dropdownArray = finalOptions.map((o) => {
      return {
        value: o.value,
        label: getFormattedMessage(o.id, o.text, true),
      }
    })
    const currentValue = dropdownArray.find((opt) => (value ? opt?.value === value : opt?.value === "")) || {
      value: value,
      label: value,
    }
    return (
      <ReactSelectionElement
        list={dropdownArray}
        placeholder={getFormattedMessage("general.select", "Select")}
        value={currentValue}
        getOptionLabel={(option) => option.label}
        disabled={dropdownArray.length === 0}
        onValueChange={(_, opt) => {
          if (opt?.value !== value) {
            setValue(opt?.value)
            onChange(opt?.value)
          }
        }}
        customClass={"lanzatable_select"}
        isMulti={false}
        isClearable={isClearable}
        divStyle={{ marginBottom: "0", fontSize: "80%" }}
        customStyles={{
          control: (base, state) => ({
            ...base,
            minHeight: "25px",
            height: "25px",
            margin: 0,
            padding: 0,
          }),
          valueContainer: (base) => ({
            ...base,
            height: "25px",
            padding: "0 8px",
          }),
          input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
          }),
          indicatorsContainer: (base) => ({
            ...base,
            height: "25px",
          }),
          clearIndicator: (base) => ({
            ...base,
            padding: "3px",
          }),
          dropdownIndicator: (base) => ({
            ...base,
            padding: "3px",
          }),
        }}
      />
    )
  }

  // For global and default filters
  return (
    <input
      className={filterType === "global" ? "form-control form-control-sm" : "form-control input_xs"}
      value={value || ""}
      type={shouldBeNumeric ? "number" : "text"}
      onChange={(e) => {
        let newValue = e.target.value
        if (shouldBeNumeric) {
          // Allow empty string or valid number
          if (newValue === "" || !isNaN(newValue)) {
            setValue(newValue)
            onChange(newValue)
          }
        } else {
          setValue(newValue)
          onChange(newValue)
        }
      }}
      placeholder={getFormattedMessage("general.search", "Search", true)}
    />
  )
}

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef()
  const resolvedRef = ref || defaultRef

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate
  }, [resolvedRef, indeterminate])

  return (
    <>
      <input type={"checkbox"} ref={resolvedRef} {...rest} />
    </>
  )
})

export const LanzaTable = forwardRef((props, ref) => {
  /*
    Custom Table build on top of 'react-table'.

    Props
    ------
      - `columns`: Column definition as per 'react-table' docs.
      - `data`: JSON data for the table
      - `initialTableOptions`: Json config for table initial state. Refer `initialState` in 'react-table' docs.
        To allow pagination, pass config param `pageSize`
      - `allowSorting`: {true/false} to enable sorting. Use column options as per 'react-table' docs.
      - `allowFilters`: {true/false} to enable column filtering. Use column options as per 'react-table' docs.
      - `allowGlobalFilter`: {true/false} to enable full text global search in data
      - `allowRowSelection`: {true/false} to enable row selection
      - `allowRowExpansion`: {true/false} to enable expandable rows
      - `expandedRowDetailsKey`: Optional row key containing an object to render in expanded section
      - `getExpandedRowDetails(rowOriginal)`: Optional callback that returns object for expanded section
        (if provided, this takes priority over `expandedRowDetailsKey`)

    Methods
    --------
      - `getSelectedRows()`: Method to get all selected rows.
      - `refresh()`: Method to force a refresh.

  */

  const {
    name, // Used when needing to troubleshoot (e.g. console.log name to see which table is having re-rendering issues)
    columns,
    onRowClick,
    isRowSelected,
    allowSorting,
    allowFilters,
    allowGlobalFilter,
    allowRowSelection,
    initialTableOptions,
    getDataPage,
    noDataMessageId,
    setDataStatus,
    extraTableFilters,
    updateMetricsWithFilter,
    allowRowExpansion,
    getExpandedRowDetails,
    expandedRowDetailsKey,
  } = props

  const canExpandRows =
    allowRowExpansion || typeof getExpandedRowDetails === "function" || expandedRowDetailsKey !== undefined
  const resolvedExpandedRowDetailsKey = expandedRowDetailsKey || "expandedRowDetails"
  const initializedAutoExpandedRowsRef = useRef(new Set())

  const getRowExpandedConfig = useCallback(
    (rowOriginal) => {
      let expandedConfig = null
      if (typeof getExpandedRowDetails === "function") {
        expandedConfig = getExpandedRowDetails(rowOriginal)
      } else {
        expandedConfig = rowOriginal?.[resolvedExpandedRowDetailsKey]
      }

      if (Array.isArray(expandedConfig)) {
        const [autoExpand = false, details = null] = expandedConfig
        return {
          autoExpand: Boolean(autoExpand),
          details: details,
        }
      }

      return {
        autoExpand: false,
        details: expandedConfig,
      }
    },
    [getExpandedRowDetails, resolvedExpandedRowDetailsKey],
  )

  const formatExpandedValue = useCallback((value) => {
    if (value === null || value === undefined || value === "") {
      return "-"
    }
    if (React.isValidElement(value)) {
      return value
    }
    if (typeof value === "object") {
      return JSON.stringify(value)
    }
    return String(value)
  }, [])

  const isDisplayableExpandedValue = useCallback((value) => {
    if (value === null || value === undefined || value === "") {
      return false
    }
    if (Array.isArray(value)) {
      return value.length > 0
    }
    if (typeof value === "object") {
      return Object.keys(value).length > 0
    }
    return true
  }, [])

  const getDisplayableExpandedEntries = useCallback(
    (details) => {
      if (!details || typeof details !== "object") {
        return []
      }
      return Object.entries(details).filter(([, value]) => isDisplayableExpandedValue(value))
    },
    [isDisplayableExpandedValue],
  )

  // Pre-compute no data message to avoid Rule of Hooks violation
  // Doing this in the registration above leads to a violation during conditional rendering due to
  // conditional usage of useIntl()
  const noDataMessage = getFormattedMessage(noDataMessageId, "No data found", true)

  let tableInitialState = { ...initialTableOptions }
  const allowPagination = "pageSize" in tableInitialState
  if (!allowPagination) {
    tableInitialState["pageSize"] = Number.MAX_SAFE_INTEGER
  }
  const [data, setData] = useState(getDataPage ? [] : props["data"])
  const [rowCount, setRowCount] = useState((data || []).length)
  useEffect(() => {
    if (!getDataPage && props.data) {
      setData(props.data)
      setRowCount(props.data.length)
    }
  }, [props.data])

  const [status, setStatus] = useState(getDataPage ? API_STATUS.LOADING : API_STATUS.SUCCESS)

  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: ({ column, preFilteredRows }) => (
        <UniversalFilter
          column={column}
          setFilter={column.setFilter}
          preFilteredRows={preFilteredRows}
          updateMetricsWithFilter={updateMetricsWithFilter}
        />
      ),
    }),
    [],
  )

  const [jumpToPage, setJumpToPage] = useState("")
  const handleJumpToPage = () => {
    const pageNumber = parseInt(jumpToPage, 10)
    if (pageNumber > 0 && pageNumber <= pageCount) {
      gotoPage(pageNumber - 1) // gotoPage is 0-indexed, so we subtract 1
    }
    setJumpToPage("") // Reset the input after jumping
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,

    // For filters
    state,
    visibleColumns,
    preGlobalFilteredRows,
    setGlobalFilter,

    // For pagination
    canPreviousPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    // For Row Select
    selectedFlatRows,

    // Common
    state: {
      // For pagination
      pageIndex,
      pageSize,
      sortBy,
      filters,
      globalFilter,
    },
  } = useTable(
    {
      columns,
      data,
      disableSortBy: !allowSorting,
      disableFilters: !allowFilters,
      defaultColumn, // Be sure to pass the defaultColumn option
      initialState: tableInitialState,
      manualPagination: getDataPage != null,
      manualGlobalFilter: getDataPage != null,
      manualFilters: getDataPage != null,
      manualSortBy: getDataPage != null,
      pageCount: Math.ceil(rowCount / tableInitialState["pageSize"]),
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetExpanded: false,
    },

    useFilters, // For column filtering
    useGlobalFilter, // For global filtering
    useSortBy, // For sorting
    useExpanded, // Use the useExpanded plugin hook
    usePagination, // For pagination

    // Row Select
    useRowSelect,
    (hooks) => {
      if (canExpandRows) {
        hooks.visibleColumns.push((columns) => [
          {
            id: "expander",
            Header: () => null,
            disableSortBy: true,
            disableFilters: true,
            Cell: ({ row }) => {
              const { details } = getRowExpandedConfig(row.original)
              const hasDetails = getDisplayableExpandedEntries(details).length > 0
              if (!hasDetails) {
                return null
              }
              return (
                <span aria-label={row.isExpanded ? "Collapse row" : "Expand row"}>
                  <span className={row.isExpanded ? "fa fa-caret-down" : "fa fa-caret-right"} />
                </span>
              )
            },
            style: {
              width: "30px",
              textAlign: "center",
            },
          },
          ...columns,
        ])
      }

      if (allowRowSelection) {
        hooks.visibleColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: "selection",
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            ),
            style: {
              width: "40px",
            },
          },
          ...columns,
        ])
      }
    },
  )

  useEffect(() => {
    if (!canExpandRows) {
      return
    }

    rows.forEach((row) => {
      const { autoExpand, details } = getRowExpandedConfig(row.original)
      if (!autoExpand) {
        return
      }

      const hasDetails = getDisplayableExpandedEntries(details).length > 0
      if (!hasDetails) {
        return
      }

      const rowKey = String(row.original?.id ?? row.id)
      if (initializedAutoExpandedRowsRef.current.has(rowKey)) {
        return
      }

      initializedAutoExpandedRowsRef.current.add(rowKey)
      if (!row.isExpanded) {
        row.toggleRowExpanded(true)
      }
    })
  }, [canExpandRows, rows, getRowExpandedConfig, getDisplayableExpandedEntries])

  // This little block is all because react-table doesn't let us dynamically change pageCount based on pageSize
  let { pageCount } = state
  pageCount = Math.ceil(rowCount / pageSize)
  const canNextPage = () => {
    const totalPages = getDataPage ? pageCount : pageOptions.length
    return pageIndex + 1 < totalPages
  }

  useEffect(() => {
    // Data Is Loading/Error-ed
    const nonSuccessStatus = status === API_STATUS.LOADING || status === API_STATUS.ERROR
    // Data is loaded, but none is available
    const dataUnavailableStatus = nonSuccessStatus || (status === API_STATUS.SUCCESS && pageCount === 0)
    setDataStatus && setDataStatus(nonSuccessStatus, dataUnavailableStatus)
  }, [status, setDataStatus, pageCount])

  // Combine table filters and filters coming from widgets/inputs outside of those directly in the table
  const [finalFilters, setFinalFilters] = useState({})
  useEffect(() => {
    const colFilters = {}
    filters.forEach((filter) => {
      colFilters[filter.id] = filter.value
    })
    setFinalFilters({ ...colFilters, ...(extraTableFilters || {}) })
  }, [filters, extraTableFilters])

  const fetchDataDebounced = useAsyncDebounce(() => fetchData(), 100)
  const fetchData = useCallback(async () => {
    const timeout = setTimeout(() => {
      setStatus(API_STATUS.LOADING) // Use timeout to prevent screen flicker
    }, 200)
    const skip = pageSize * pageIndex
    const [sort, order] = sortBy.length ? [sortBy[0].id, sortBy[0].desc ? "desc" : "asc"] : [null, null]
    await getDataPage({
      skip: skip,
      limit: pageSize,
      sort: sort,
      order: order,
      filters: finalFilters,
      globalFilter: globalFilter,
    })
      .then((d) => {
        setData(d.data)
        setRowCount(d.rowCount)
        if (d.rowCount < skip) {
          // Requested a page that is out of range, jump back to page 0
          gotoPage(0)
        }
        setStatus(API_STATUS.SUCCESS)
      })
      .catch((error) => {
        setStatus(API_STATUS.ERROR)
        console.error("Error while fetching", error)
        logToServer(error)
      })
      .finally(() => {
        clearTimeout(timeout)
      })
  }, [pageSize, pageIndex, sortBy, finalFilters, globalFilter])

  useEffect(() => {
    getDataPage && fetchDataDebounced()
  }, [pageSize, pageIndex, sortBy, finalFilters, globalFilter])

  useImperativeHandle(ref, () => ({
    refresh() {
      /*
      Method to force the table to reload data.

      Usage:
          ref_table.current.refresh();

    */
      fetchDataDebounced()
    },
    getSelectedRows() {
      /*
      Method to get all selected rows.
      Return list of original selected row's data.

      Usage:
          ref_table.current.getSelectedRows();

    */
      return selectedFlatRows.map((d) => d.original)
    },
  }))

  let recordRange = {}
  let pageRange = {}
  if (getDataPage) {
    recordRange = {
      min: pageCount > 0 ? pageSize * pageIndex + 1 : 0,
      max: Math.min(pageSize * pageIndex + pageSize, rowCount),
      count: rowCount,
    }
    pageRange = {
      current_page_num: pageCount > 0 ? pageIndex + 1 : 0,
      total_page_num: pageCount,
    }
  } else {
    recordRange = {
      min: pageSize * pageIndex + 1,
      max: pageSize * pageIndex + page.length,
      count: rows.length,
    }
    pageRange = {
      current_page_num: pageIndex + 1,
      total_page_num: pageOptions.length,
    }
  }

  // We use display:none for the table to prevent it clobbering our search/sort settings in case of no hits
  return (
    <>
      {status === API_STATUS.LOADING && <PageLoadingPanel />}
      {status === API_STATUS.ERROR && <PageErrorPanel attemptReload={fetchDataDebounced} />}
      {status === API_STATUS.SUCCESS && pageCount === 0 && noDataMessageId && (
        <div align={"center"} style={{ fontSize: "30px", marginTop: "20px", marginBottom: "20px" }}>
          <i className={"fa fa-info-circle"} style={{ fontSize: "35px" }} />
          <h5>{noDataMessage}</h5>
        </div>
      )}
      <table
        style={{
          visibility: status === API_STATUS.SUCCESS ? "visible" : "hidden",
          ...props["style"],
        }}
        className={props["className"]}
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup, hg) => (
            <Fragment key={hg}>
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, c) => (
                  <th
                    valign={"top"}
                    {...column.getHeaderProps([
                      {
                        className: column.className,
                        style: column.style,
                      },
                      column.getSortByToggleProps(),
                    ])}
                    key={c}
                  >
                    {column.render("Header")}
                    {column.canSort && (
                      <span
                        style={{
                          marginLeft: "4px",
                        }}
                      >
                        <span
                          className={`fa fa-sort${!column.isSorted ? "" : column.isSortedDesc ? "-desc" : "-asc"}`}
                          style={!column.isSorted ? { opacity: 0.5 } : {}}
                        />
                      </span>
                    )}
                  </th>
                ))}
              </tr>

              {/* Render the columns filter UI */}
              {allowFilters && (
                <tr>
                  {headerGroup.headers.map((column, c) => (
                    <th key={c}>
                      <div>{column.canFilter ? column.render("Filter") : null}</div>
                    </th>
                  ))}
                </tr>
              )}
            </Fragment>
          ))}

          {/*Global Filter*/}
          {allowGlobalFilter && (
            <tr>
              <td colSpan={visibleColumns.length} style={{ textAlign: "left" }}>
                <UniversalFilter
                  filterType="global"
                  globalFilter={state.globalFilter}
                  setGlobalFilter={setGlobalFilter}
                  preFilteredRows={preGlobalFilteredRows}
                  updateMetricsWithFilter={updateMetricsWithFilter}
                />
              </td>
            </tr>
          )}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row)
            const rowProps = row.getRowProps()
            const rowIsSelected = isRowSelected ? isRowSelected(row.original) : false
            const { details: expandedDetails } = getRowExpandedConfig(row.original)
            const expandedEntries = getDisplayableExpandedEntries(expandedDetails)
            const hasExpandableContent = expandedEntries.length > 0
            const handleRowClick = (event) => {
              const isInteractiveElement = event.target.closest(
                "button, a, input, select, textarea, label, [role='button']",
              )
              if (isInteractiveElement) {
                return
              }
              onRowClick && onRowClick(row.original, event)
            }
            return (
              <Fragment key={i}>
                <tr
                  {...rowProps}
                  onClick={onRowClick ? handleRowClick : rowProps.onClick}
                  style={{
                    ...(rowProps?.style || {}),
                    ...(onRowClick ? { cursor: "pointer" } : {}),
                    ...(rowIsSelected
                      ? {
                          backgroundColor: "var(--lanzalake-blue-bright-darker)",
                          "--bs-table-bg": "var(--lanzalake-blue-bright-darker)",
                        }
                      : {}),
                  }}
                >
                  {row.cells.map((cell, c) => {
                    const isExpanderCell = cell.column.id === "expander"
                    const expanderCellProps =
                      isExpanderCell && hasExpandableContent
                        ? {
                            onClick: (event) => {
                              event.stopPropagation()
                              const toggleProps = row.getToggleRowExpandedProps()
                              toggleProps.onClick && toggleProps.onClick(event)
                            },
                            style: {
                              cursor: "pointer",
                            },
                            "data-row-expander": "true",
                          }
                        : isExpanderCell
                          ? {
                              "data-row-expander": "true",
                            }
                          : {}
                    return (
                      <td
                        {...cell.getCellProps([
                          {
                            className: cell.column.className,
                            style: cell.column.style,
                          },
                          expanderCellProps,
                        ])}
                        key={c}
                      >
                        {cell.render("Cell")}
                      </td>
                    )
                  })}
                </tr>
                {row.isExpanded && expandedEntries.length > 0 && (
                  <tr style={{ backgroundColor: "var(--lanzalake-grey-light-2)" }}>
                    <td colSpan={visibleColumns.length}>
                      {expandedEntries.map(([key, value], i) => (
                        <div className={"row"} key={`${key}${i}`}>
                          <div className={"col-1"} />
                          <div className={"col-2"}>
                            <strong>{key}</strong>
                          </div>
                          <div className={"col-8"}>{formatExpandedValue(value)}</div>
                          <div className={"col-1"} />
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>

        {allowPagination && (
          <tfoot>
            <tr>
              <td colSpan={visibleColumns.length} style={{ padding: "5px 10px" }}>
                <div>
                  <table style={{ width: "100%" }} className={"table-sm"}>
                    <tbody>
                      <tr>
                        <td style={{ width: "25%", border: "none" }}>
                          <span>{getFormattedMessage("general.show", "Show", true)}:</span>
                          &nbsp;
                          <select
                            className={"form-select form-select-sm"}
                            style={{ width: "50%", display: "inline-block" }}
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(Number(e.target.value))
                            }}
                          >
                            {[5, 10, 20, 30, 40, 50, 100].map((pageSize) => (
                              <option key={pageSize} value={pageSize}>
                                {pageSize}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ width: "50%", border: "none" }} align={"center"}>
                          <Button
                            variant={"outline-primary"}
                            size={"sm"}
                            onClick={() => gotoPage(0)}
                            disabled={!canPreviousPage}
                          >
                            <span className={"fa fa-fast-backward"} />
                          </Button>
                          &nbsp;
                          <Button
                            variant={"outline-primary"}
                            size={"sm"}
                            onClick={() => previousPage()}
                            disabled={!canPreviousPage}
                          >
                            <span className={"fa fa-caret-left"} />
                          </Button>
                          <span style={{ margin: "0px 10px" }}>
                            {getFormattedMessage(
                              "general.page.count.of.count",
                              `Page ${pageRange.current_page_num} of ${pageRange.total_page_num}`,
                              false,
                              null,
                              pageRange,
                            )}
                          </span>
                          <Button
                            variant={"outline-primary"}
                            size={"sm"}
                            onClick={() => nextPage()}
                            disabled={!canNextPage()}
                          >
                            <span className={"fa fa-caret-right"} />
                          </Button>
                          &nbsp;
                          <Button
                            variant={"outline-primary"}
                            size={"sm"}
                            onClick={() => gotoPage(pageCount - 1)}
                            disabled={!canNextPage()}
                          >
                            <span className={"fa fa-fast-forward"} />
                          </Button>
                          <span style={{ marginLeft: "10px" }}>
                            <input
                              type="number"
                              min="1"
                              max={pageCount}
                              value={jumpToPage}
                              onChange={(e) => setJumpToPage(e.target.value)}
                              style={{ width: "50px", marginRight: "5px" }}
                            />
                            <Button
                              variant={"outline-primary"}
                              size={"sm"}
                              onClick={handleJumpToPage}
                              disabled={!jumpToPage}
                            >
                              <span className={"fa fa-share"} />
                            </Button>
                          </span>
                        </td>
                        <td style={{ width: "25%", border: "none" }} align={"right"}>
                          {getFormattedMessage(
                            "general.record.of.count",
                            `View ${recordRange.min} - ${recordRange.max} of ${recordRange.count}`,
                            false,
                            null,
                            recordRange,
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </>
  )
})
